import { useEffect, useLayoutEffect, useRef, useState, type JSX } from 'react';
import type { LrcLine } from '../lib/types';
import { IconExpand, IconClose } from './icons';

export interface LyricsStripProps {
  synced: LrcLine[];
  currentTime: number;
  fallback?: string;
  onSeek(t: number): void;
  immersive: boolean;
  onImmersiveChange(v: boolean): void;
}

function activeIndex(synced: LrcLine[], t: number): number {
  let idx = 0;
  for (let i = 0; i < synced.length; i++) {
    if (synced[i].t <= t + 0.15) idx = i;
    else break;
  }
  return idx;
}

function useEnter(): boolean {
  const [entered, setEntered] = useState(false);
  useLayoutEffect(() => {
    const raf = requestAnimationFrame(() => setEntered(true));
    return () => cancelAnimationFrame(raf);
  }, []);
  return entered;
}

function ImmersivePanel({
  synced,
  idx,
  onSeek,
  onClose,
  reduced,
}: {
  synced: LrcLine[];
  idx: number;
  onSeek(t: number): void;
  onClose(): void;
  reduced: boolean;
}): JSX.Element {
  const entered = useEnter();
  const containerRef = useRef<HTMLDivElement>(null);
  const currentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const container = containerRef.current;
    const line = currentRef.current;
    if (!container || !line) return;
    const top = line.offsetTop - (container.clientHeight - line.offsetHeight) / 2;
    container.scrollTo({ top, behavior: reduced ? 'auto' : 'smooth' });
  }, [idx, reduced]);

  return (
    <div
      className="sheet-spring relative"
      style={{ opacity: entered ? 1 : 0, transform: entered ? 'translateY(0)' : 'translateY(8px)' }}
    >
      <div
        ref={containerRef}
        className="mx-auto max-w-xl overflow-y-auto py-2"
        style={{
          maxHeight: 'min(46vh, 360px)',
          WebkitMaskImage:
            'linear-gradient(to bottom, transparent 0, #000 20px, #000 calc(100% - 20px), transparent 100%)',
          maskImage: 'linear-gradient(to bottom, transparent 0, #000 20px, #000 calc(100% - 20px), transparent 100%)',
        }}
      >
        {synced.map((line, i) => (
          <div
            key={i}
            ref={i === idx ? currentRef : undefined}
            role="button"
            tabIndex={0}
            onClick={() => onSeek(line.t)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onSeek(line.t);
              }
            }}
            className={`font-display cursor-pointer truncate px-8 py-1.5 text-center transition-[color,font-size] duration-200 ${
              i === idx ? 'text-base font-semibold text-fg' : 'text-sm text-fg/40'
            }`}
          >
            {line.text || '♪'}
          </div>
        ))}
      </div>
      <button
        aria-label="收起歌词"
        onClick={onClose}
        className="glass absolute right-0 top-0 grid h-7 w-7 place-items-center rounded-full text-faint transition hover:text-fg"
      >
        <IconClose className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}

export function LyricsStrip({
  synced,
  currentTime,
  fallback,
  onSeek,
  immersive,
  onImmersiveChange,
}: LyricsStripProps): JSX.Element | null {
  const [reduced] = useState(
    () => typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches,
  );
  const hasLyrics = synced.length > 0;
  const idx = hasLyrics ? activeIndex(synced, currentTime) : -1;

  const [expanded, setExpanded] = useState(false);
  const collapseTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const idleTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const lastPointer = useRef<string>('mouse');

  useEffect(() => {
    if (!hasLyrics) setExpanded(false);
  }, [hasLyrics]);

  useEffect(
    () => () => {
      if (collapseTimer.current) clearTimeout(collapseTimer.current);
      if (idleTimer.current) clearTimeout(idleTimer.current);
    },
    [],
  );

  if (!hasLyrics) {
    return fallback ? (
      <div className="mx-auto mt-3 max-w-xl truncate px-4 text-center text-sm font-light text-fg/55">{fallback}</div>
    ) : null;
  }

  if (immersive) {
    return (
      <ImmersivePanel
        synced={synced}
        idx={idx}
        onSeek={onSeek}
        onClose={() => onImmersiveChange(false)}
        reduced={reduced}
      />
    );
  }

  const armIdle = () => {
    if (idleTimer.current) clearTimeout(idleTimer.current);
    idleTimer.current = setTimeout(() => setExpanded(false), 4000);
  };

  const onPointerDownCapture = (e: React.PointerEvent<HTMLDivElement>) => {
    lastPointer.current = e.pointerType;
  };
  const onPointerEnter = (e: React.PointerEvent<HTMLDivElement>) => {
    if (e.pointerType !== 'mouse') return;
    if (collapseTimer.current) clearTimeout(collapseTimer.current);
    setExpanded(true);
  };
  const onPointerLeave = (e: React.PointerEvent<HTMLDivElement>) => {
    if (e.pointerType !== 'mouse') return;
    if (collapseTimer.current) clearTimeout(collapseTimer.current);
    collapseTimer.current = setTimeout(() => setExpanded(false), 2000);
  };

  const clickLine = (t: number) => {
    const touch = lastPointer.current !== 'mouse';
    if (!expanded) {
      setExpanded(true);
      if (touch) armIdle();
      return;
    }
    onSeek(t);
    if (touch) armIdle();
  };

  const transitionCls = reduced ? '' : 'transition-all duration-[240ms]';

  return (
    <div
      className="relative mx-auto mt-3 max-w-xl px-8"
      onPointerDownCapture={onPointerDownCapture}
      onPointerEnter={onPointerEnter}
      onPointerLeave={onPointerLeave}
    >
      {!expanded ? (
        <div
          role="button"
          tabIndex={0}
          onClick={() => clickLine(synced[idx]?.t ?? currentTime)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              clickLine(synced[idx]?.t ?? currentTime);
            }
          }}
          className="font-display cursor-pointer truncate text-center text-sm font-medium text-fg/80"
          style={{ textShadow: '0 0 20px color-mix(in srgb, var(--fg) 30%, transparent)' }}
        >
          {synced[idx]?.text || '♪'}
        </div>
      ) : (
        <div className="flex flex-col items-center gap-0.5">
          {[idx - 1, idx, idx + 1].map((i) => {
            const line = synced[i];
            const isCurrent = i === idx;
            return (
              <div
                key={i}
                role={line ? 'button' : undefined}
                tabIndex={line ? 0 : undefined}
                onClick={() => line && clickLine(line.t)}
                onKeyDown={(e) => {
                  if (!line) return;
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    clickLine(line.t);
                  }
                }}
                className={`font-display w-full truncate text-center ${transitionCls} ${line ? 'cursor-pointer' : ''} ${
                  isCurrent ? 'text-base font-semibold text-fg' : 'text-sm text-fg/35'
                }`}
                style={{ visibility: line ? 'visible' : 'hidden' }}
              >
                {line?.text ?? '♪'}
              </div>
            );
          })}
        </div>
      )}
      <button
        aria-label="展开完整歌词"
        onClick={() => onImmersiveChange(true)}
        className="absolute right-1 top-1/2 -translate-y-1/2 rounded-full p-1 text-faint opacity-60 transition hover:text-fg hover:opacity-100"
      >
        <IconExpand className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}
